import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Eye, FileText, MagnifyingGlass, ArrowClockwise, Download, Receipt, FileXls } from '@phosphor-icons/react';
import { ReceiptDialog } from '../../components/admin/ReceiptDialog';
import { useAuth } from '../../context/AuthContext';
import { formatAnyDate } from '../../utils/dateFormat';

const STATUS_TABS = [
  { k: 'all', label: 'All' },
  { k: 'draft', label: 'Draft' },
  { k: 'final', label: 'Final' },
  { k: 'partially_converted', label: 'Partial' },
  { k: 'converted', label: 'Converted' },
];

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

function MobileQuotations() {
  const { user } = useAuth();
  const isSales = user?.role === 'sales';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [receiptFor, setReceiptFor] = useState(null);
  const [filters, setFilters] = useState({
    executive_name: '', customer_name: '', customer_mobile: '',
    quotation_number: '', quotation_date_from: '', quotation_date_to: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (tab !== 'all') params.status = tab;
      // strip empty
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get('/api/admin/mobile-quotations', { params });
      setRows(data.quotations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { load(); }, [load]);

  const openPdf = (qn) => {
    window.open(`/api/admin/mobile-quotations/${encodeURIComponent(qn)}/pdf`, '_blank');
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (tab !== 'all') params.set('status', tab);
    window.open(`/api/admin/mobile-quotations/export?${params.toString()}`, '_blank');
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Mobile Quotations</h1>
            <p className="text-sm text-zinc-500 mt-1">Read-only view of quotations created from the mobile app.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-excel-btn" variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={exportExcel}>
              <FileXls size={16} className="mr-1" /> Export Excel
            </Button>
            <Button data-testid="refresh-btn" variant="outline" onClick={load}>
              <ArrowClockwise size={16} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Input
              data-testid="filter-quotation-number"
              placeholder="Quotation #"
              value={filters.quotation_number}
              onChange={(e) => setFilters({ ...filters, quotation_number: e.target.value })}
            />
            <Input
              data-testid="filter-customer-name"
              placeholder="Customer Name"
              value={filters.customer_name}
              onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })}
            />
            <Input
              data-testid="filter-customer-mobile"
              placeholder="Customer Mobile"
              value={filters.customer_mobile}
              onChange={(e) => setFilters({ ...filters, customer_mobile: e.target.value })}
            />
            <Input
              data-testid="filter-executive"
              placeholder="Executive Name"
              value={filters.executive_name}
              onChange={(e) => setFilters({ ...filters, executive_name: e.target.value })}
            />
            <Input
              data-testid="filter-date-from"
              type="date"
              value={filters.quotation_date_from}
              onChange={(e) => setFilters({ ...filters, quotation_date_from: e.target.value })}
            />
            <Input
              data-testid="filter-date-to"
              type="date"
              value={filters.quotation_date_to}
              onChange={(e) => setFilters({ ...filters, quotation_date_to: e.target.value })}
            />
            <Button data-testid="apply-filter-btn" onClick={load} className="bg-orange-500 hover:bg-orange-600 text-white">
              <MagnifyingGlass size={16} className="mr-1" /> Apply
            </Button>
            <Button
              variant="outline"
              data-testid="clear-filter-btn"
              onClick={() => setFilters({ executive_name: '', customer_name: '', customer_mobile: '', quotation_number: '', quotation_date_from: '', quotation_date_to: '' })}
            >
              Clear
            </Button>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-zinc-200">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.k ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >{t.label}</button>
          ))}
        </div>

        {/* Table */}
        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Quotation #</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Customer</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Mobile</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Executive</th>
                  <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">SQFT</th>
                  <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Total</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={9} className="p-6 text-center text-zinc-400">No quotations found</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.quotation_number} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`q-row-${r.quotation_number}`}>
                    <td className="p-3 font-mono">
                      <Link to={`/admin/mobile-quotations/${encodeURIComponent(r.quotation_number)}`} className="text-blue-600 hover:underline">
                        {r.quotation_number}
                      </Link>
                      <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100 text-[9px]">MOBILE</Badge>
                    </td>
                    <td className="p-3 text-xs text-zinc-700">{formatAnyDate(r.quotation_date) || '—'}</td>
                    <td className="p-3">{r.customer_name || '—'}</td>
                    <td className="p-3 font-mono text-xs">{r.customer_mobile || '—'}</td>
                    <td className="p-3 text-xs">{r.executive_name || '—'}</td>
                    <td className="p-3 text-right">{Number(r.total_sqft || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-bold">{fmtINR(r.total_amount)}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className="text-xs uppercase">{r.status || 'draft'}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Link to={`/admin/mobile-quotations/${encodeURIComponent(r.quotation_number)}`}>
                          <Button data-testid={`view-${r.quotation_number}`} variant="ghost" size="sm"><Eye size={16} /></Button>
                        </Link>
                        <Button
                          data-testid={`pdf-${r.quotation_number}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => openPdf(r.quotation_number)}
                          title="PDF"
                        >
                          <FileText size={16} />
                        </Button>
                        <a
                          href={`/api/admin/mobile-quotations/${encodeURIComponent(r.quotation_number)}/pdf`}
                          download
                          title="Download"
                        >
                          <Button variant="ghost" size="sm"><Download size={16} /></Button>
                        </a>
                        {!isSales && (
                          <Button
                            data-testid={`receipt-${r.quotation_number}`}
                            variant="ghost"
                            size="sm"
                            title="Record Receipt"
                            className="text-emerald-700"
                            onClick={() => setReceiptFor(r)}
                          >
                            <Receipt size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <ReceiptDialog
        open={!!receiptFor}
        onOpenChange={(o) => !o && setReceiptFor(null)}
        quotationNo={receiptFor?.quotation_number}
        prefill={{
          customer_name: receiptFor?.customer_name,
          customer_mobile: receiptFor?.customer_mobile,
          executive_name: receiptFor?.executive_name,
        }}
      />
    </Layout>
  );
}

export default MobileQuotations;
export { MobileQuotations };
